import {
  add,
  cross,
  dot,
  length,
  normalize,
  rotateAbout,
  scale,
  subtract,
  type Vec3,
  vec3,
} from './Vec3';

/**
 * The part of a solid this file needs: where its corners are, what joins them, and what bounds
 * it. Narrower than {@link Mesh} on purpose, so a caller holding only an evaluated solid — which
 * carries exactly these three — can ask without rebuilding the mesh.
 */
export interface Body {
  readonly vertices: readonly Vec3[];
  readonly faces: readonly (readonly number[])[];
  readonly edges: readonly { readonly a: number; readonly b: number }[];
}

/**
 * Planes in space, and what a figure asks of them.
 *
 * ## Why this exists
 *
 * "đó đã là best practice cho ứng dụng vẽ hình chưa, đã đúng với cách dựng hình trong thực tế
 * của giáo viên chưa?" — no, and this file is the answer.
 *
 * Until now a plane was a *style name*. A section through a solid was drawn by typing the answer
 * as a polygon: `polygon A M N D`. That works only because the person typing had already solved
 * the problem, and it is unchecked — four points 67 units out of any common plane draw as a
 * convincing shaded quadrilateral with no complaint. The figure could be geometrically false and
 * look perfect, which is the one thing this editor exists not to allow (ADR-0003).
 *
 * A teacher thinks in three steps: name a plane, ask for a section, ask for an intersection
 * line. Two of those the program could not answer at all. So the plane becomes a real object
 * with real mathematics behind it, and the answers are computed rather than typed.
 *
 * ## The representation
 *
 * A point and a unit normal. Not three points: three points are one *way* to name a plane and
 * the plane does not remember which, so a plane through a point parallel to another plane is the
 * same kind of thing and the rest of this file needs no second case for it.
 */

export interface Plane {
  /** A point the plane passes through. */
  readonly origin: Vec3;
  /** Unit normal. Its sign is arbitrary and no caller may depend on it. */
  readonly normal: Vec3;
}

/** Below this a determinant is noise rather than geometry, in the units a figure is drawn in. */
const FLAT = 1e-7;

/**
 * The plane through three points, or `null` when they do not name one.
 *
 * Three *collinear* points lie on infinitely many planes, so there is no answer to give — the
 * same treatment every other degenerate construction gets here: no value, and the figure simply
 * has nothing there until the points are moved apart.
 */
export function planeThrough(a: Vec3, b: Vec3, c: Vec3): Plane | null {
  const normal = cross(subtract(b, a), subtract(c, a));
  const size = length(normal);

  // Scaled by the triangle's own size, so the test means "these three are nearly in a line"
  // rather than "this figure is drawn small".
  const spread = Math.max(length(subtract(b, a)), length(subtract(c, a)));
  if (spread <= 0 || size / (spread * spread) < FLAT) return null;

  return { origin: a, normal: scale(normal, 1 / size) };
}

/** The plane through a point, parallel to another. "Mặt phẳng qua M song song với (ABC)". */
export function planeParallel(through: Vec3, to: Plane): Plane {
  return { origin: through, normal: to.normal };
}

/**
 * A plane turned about a line lying in it. "Mặt phẳng qua AB, hợp với (ABCD) một góc 60°".
 *
 * ## Why a turn and not an angle
 *
 * Because a stated angle is not a construction. "A plane at sixty degrees to that one" names a
 * whole cone of planes and says nothing about which; the figure would have to guess, and a guess
 * that looks plausible is what this file exists to avoid. A *turn* names one: this plane, this
 * axis, this many degrees. It is also the form GeoGebra settled on — `Rotate(object, angle, axis)`
 * — where the CAD form, "plane at an angle to a face", has left Onshape's users unable to say for
 * nine years which reference the angle is measured from.
 *
 * ## Why the axis must lie in the plane
 *
 * Turning about a line *outside* the plane is a perfectly good rigid motion, and it lifts the
 * plane away rather than tilting it: the result no longer touches the axis, and the figure shows
 * a section sliding off through the solid for no reason a reader can see. Every problem that says
 * "mặt phẳng qua AB" means AB is in both. {@link axisInPlane} is the check, kept separate so a
 * caller can refuse with its own words instead of getting a silent `null`.
 *
 * The turned plane keeps the axis point as its origin, so it genuinely still contains the axis —
 * not approximately, but by construction.
 */
export function turnPlane(base: Plane, from: Vec3, to: Vec3, radians: number): Plane | null {
  const axis = subtract(to, from);
  const size = length(axis);
  if (size < FLAT) return null;

  return {
    origin: from,
    normal: normalize(rotateAbout(base.normal, scale(axis, 1 / size), radians)),
  };
}

/**
 * Whether a line lies in a plane, to a tolerance scaled by how big the figure is.
 *
 * Scaled for the same reason {@link coplanar} scales: an absolute epsilon means a figure drawn at
 * three hundred units is judged by a different standard from the same figure drawn at three, and
 * the second one starts refusing constructions that are exactly as correct.
 */
export function axisInPlane(plane: Plane, from: Vec3, to: Vec3, tolerance = 1e-6): boolean {
  const scaleOf = Math.max(1, length(subtract(to, from)), length(subtract(from, plane.origin)));
  const room = tolerance * scaleOf;
  return (
    Math.abs(signedDistance(plane, from)) <= room && Math.abs(signedDistance(plane, to)) <= room
  );
}

/**
 * Two perpendicular unit directions lying in a plane — the plane's own axes.
 *
 * ## Why not {@link frameIn}
 *
 * `frameIn` seeds from the *smallest component of the normal*, which is the right choice for a
 * one-off circle and the wrong one for anything a user watches change. Tip a plane slowly and the
 * smallest component changes rank; the seed jumps to a different world axis, and the basis — with
 * whatever is drawn on it — swings by ninety degrees for an input that moved by a hundredth of a
 * degree. A teacher dragging the tilt sees the plane flip, and reads it as a fault.
 *
 * ## Why the seed is `x` and not `up`
 *
 * Seeding from up was the first attempt, on the reasoning that it gives a plane drawn the way a
 * hand draws one. It is worse, and the measurement says how much: `cross(normal, up)` **reverses**
 * as the plane passes through level, so a corner of a 200-unit plane moved **565 units** between
 * tilt 0° and tilt 1° — a flip at exactly the angle every figure starts from.
 *
 * No seed is continuous everywhere; a sphere admits no continuous tangent field, so the flip can
 * only be *moved*, never removed. `x` puts it where a tilt never goes: tilting turns the plane
 * about `x`, so its normal stays square to `x` and the seed never degenerates. The fallback to
 * `up` is reached only by a plane standing on that axis, which no tilt produces.
 */
export function planeBasis(normal: Vec3): readonly [Vec3, Vec3] {
  const alongX = cross(normal, vec3(1, 0, 0));
  const seeded = length(alongX) < FLAT ? cross(normal, UP) : alongX;

  const size = length(seeded);
  // A zero normal names no plane; any pair will do and none of them means anything.
  const u = size < FLAT ? vec3(1, 0, 0) : scale(seeded, 1 / size);
  return [u, normalize(cross(normal, u))];
}

/** How far a point stands from a plane, signed by which side it is on. */
export function signedDistance(plane: Plane, point: Vec3): number {
  return dot(plane.normal, subtract(point, plane.origin));
}

/** Whether every point lies on one plane, to a tolerance scaled by how big the figure is. */
export function coplanar(points: readonly Vec3[], tolerance = 1e-6): boolean {
  return offPlane(points) <= tolerance * Math.max(1, spanOf(points));
}

/**
 * How far the points stray from the best plane through the first three, in world units.
 *
 * Zero for a genuine section. Reported as a distance rather than as a yes-or-no so the interface
 * can say *how* wrong a figure is: "67 units out" tells a teacher they mistyped a vertex, where
 * "not coplanar" leaves them hunting.
 */
export function offPlane(points: readonly Vec3[]): number {
  if (points.length < 4) return 0;

  // The first three that are not collinear; a leading collinear run is not the figure's fault.
  const [first, second] = points;
  if (first === undefined || second === undefined) return 0;

  let base: Plane | null = null;
  for (const point of points.slice(2)) {
    base ??= planeThrough(first, second, point);
  }
  if (base === null) return 0;

  return Math.max(...points.map((point) => Math.abs(signedDistance(base, point))));
}

/** The largest distance between any two of the points. Used to scale a tolerance. */
function spanOf(points: readonly Vec3[]): number {
  let widest = 0;
  for (const a of points) {
    for (const b of points) widest = Math.max(widest, length(subtract(a, b)));
  }
  return widest;
}

/**
 * The foot of the perpendicular from a point to a plane.
 *
 * The construction the written reference used to say was not needed — "there is no `foot` onto a
 * *plane*, and there does not need to be" — which was true only for as long as a plane was a
 * style name. It is the commonest thing a solid-geometry problem asks after the section itself:
 * the distance from a point to a plane, the height of a pyramid onto its base, the foot `H` that
 * every "khoảng cách từ S đến (ABCD)" is about.
 *
 * Always defined, with no degenerate case: a plane has a normal, and every point has exactly one
 * nearest point on it — the point itself when it is already there.
 */
export function footOnPlane(plane: Plane, point: Vec3): Vec3 {
  return subtract(point, scale(plane.normal, signedDistance(plane, point)));
}

/**
 * The angle between two planes, in radians, from 0 to a right angle.
 *
 * The *dihedral* angle a problem means by "góc giữa (SBC) và (ABCD)", which is the acute one:
 * two planes make two supplementary angles and a paper always states the smaller. Reporting the
 * signed angle between the normals would give an answer that flips when a solid is turned round,
 * for a quantity that has not changed.
 *
 * Zero for parallel planes, which is the right answer and the one that makes "are these
 * parallel?" a question about a number rather than a separate predicate.
 */
export function angleBetween(a: Plane, b: Plane): number {
  const cosine = Math.min(1, Math.abs(dot(a.normal, b.normal)));
  return Math.acos(cosine);
}

/**
 * Where two planes meet: a point on the line and its direction, or `null` when they never do.
 *
 * `null` covers both parallel and identical, and deliberately does not distinguish them. A
 * figure has nothing to draw in either case, and a program that drew *something* for coincident
 * planes would be inventing a line the geometry does not have.
 *
 * The point returned is the one nearest the origin of the two planes' own frame, which keeps it
 * near the figure instead of thousands of units away where a nearly-parallel pair would put it.
 */
export function meetPlanes(a: Plane, b: Plane): { origin: Vec3; direction: Vec3 } | null {
  const direction = cross(a.normal, b.normal);
  const size = length(direction);
  if (size < FLAT) return null;

  /*
   * The standard closed form: the point on both planes lying in the plane spanned by the two
   * normals. Solving a 3×3 system would do as well and needs a pivot strategy to stay stable;
   * this needs none.
   */
  const da = dot(a.normal, a.origin);
  const db = dot(b.normal, b.origin);
  const origin = scale(
    add(scale(cross(b.normal, direction), da), scale(cross(direction, a.normal), db)),
    1 / (size * size)
  );

  return { origin, direction: scale(direction, 1 / size) };
}

/**
 * The section of a convex mesh by a plane: the polygon where the plane cuts it.
 *
 * ## The algorithm, and why this one
 *
 * Every edge of the solid is tested for a crossing, and the crossings are then sorted by angle
 * about the plane's normal. For a **convex** solid that is exact and complete: the section of a
 * convex body by a plane is a convex polygon, its vertices lie on the body's edges, and sorting
 * them radially about their own centre puts them in the order the boundary visits them.
 *
 * The usual alternative — walking face to face, taking each face's chord in turn — needs no sort
 * and handles non-convex bodies, and it needs careful cases for a plane through a vertex or
 * along a face. Every solid this editor draws is convex, so the simpler method is the right one
 * and there is no case to get wrong.
 *
 * A vertex *on* the plane is counted once, not twice, which is the whole of the special-casing
 * here: a plane through a corner of a cube otherwise produces a doubled vertex and a section
 * with a zero-length edge in it.
 *
 * Fewer than three points means the plane touches the solid at a corner or an edge or misses it,
 * and there is no section to draw.
 */
export function sectionOf(mesh: Body, plane: Plane): readonly Vec3[] {
  const scale2 = Math.max(1, spanOf(mesh.vertices));
  const near = 1e-9 * scale2;

  const points: Vec3[] = [];
  const push = (point: Vec3): void => {
    if (points.some((seen) => length(subtract(seen, point)) < near)) return;
    points.push(point);
  };

  for (const vertex of mesh.vertices) {
    if (Math.abs(signedDistance(plane, vertex)) < near) push(vertex);
  }

  for (const edge of mesh.edges) {
    const from = mesh.vertices[edge.a];
    const to = mesh.vertices[edge.b];
    if (from === undefined || to === undefined) continue;

    const here = signedDistance(plane, from);
    const there = signedDistance(plane, to);
    // Strictly opposite sides. An endpoint on the plane was already taken above, and taking it
    // again here is what produces the doubled vertex.
    if (Math.abs(here) < near || Math.abs(there) < near) continue;
    if (here > 0 === there > 0) continue;

    const t = here / (here - there);
    push(add(from, scale(subtract(to, from), t)));
  }

  if (points.length < 3) return [];
  return sortAround(points, plane.normal);
}

/**
 * A round solid, as the numbers a section of it is worked out from.
 *
 * `taper` is the top radius as a fraction of the bottom: 1 for a cylinder, 0 for a cone, and
 * whatever the user set for a frustum. A ball says so, because every plane cuts a ball in a
 * circle and none of the tapering arithmetic applies to it.
 */
export interface RoundBody {
  readonly ball: boolean;
  /** Half-extents along x, y and z. The axis is y, as every solid here is built. */
  readonly rx: number;
  readonly ry: number;
  readonly rz: number;
  readonly taper: number;
}

/**
 * The section of a round solid: a circle in space, or `null` when there is not one.
 *
 * ## Why only some planes are answered
 *
 * Because only some of them cut a *circle*, and this editor does not draw a wrong answer
 * convincingly. A ball is cut in a circle by every plane. A cone or a cylinder is cut in a circle
 * only by a plane square to its axis — "mặt phẳng song song với đáy", which is what the syllabus
 * asks for and very nearly all it asks for. Cut obliquely, a cylinder gives an ellipse and a cone
 * gives an ellipse, a parabola or a hyperbola, each of them truncated where it runs off the ends
 * — a boundary of mixed arcs and chords that no value in this editor can express, and that a
 * polygon through sampled points would misrepresent as flat.
 *
 * A plane *containing* the axis is a different answer again — a triangle, a rectangle or a
 * trapezium — and is handled by {@link axialSection}, not here.
 *
 * The answer is a circle in space: a centre, and two perpendicular radius vectors of equal
 * length. The caller projects them, which turns the circle into an exact ellipse rather than
 * into the chain of chords a tessellation would have given.
 */
export function roundSection(
  body: RoundBody,
  plane: Plane
): { centre: Vec3; u: Vec3; v: Vec3 } | null {
  if (body.ball) {
    const away = signedDistance(plane, ZERO);
    const radius = Math.sqrt(Math.max(0, body.rx * body.rx - away * away));
    // Tangent, or clear of the ball altogether: a point is not a section.
    if (radius < FLAT) return null;

    const centre = footOnPlane(plane, ZERO);
    const [u, v] = frameIn(plane, radius);
    // An ellipsoid is not a ball, and its sections are not circles; only the round case is.
    return body.rx === body.ry && body.rx === body.rz ? { centre, u, v } : null;
  }

  // Square to the axis, and nothing else. See above.
  const along = Math.abs(dot(plane.normal, UP));
  if (along < 1 - FLAT) return null;

  const y = plane.origin.y;
  if (y < -body.ry - FLAT || y > body.ry + FLAT) return null;

  /*
   * The radius at that height, interpolated from the bottom to the top. A cone is `taper = 0`
   * and needs no case of its own: the formula runs its radius to nothing at the apex, which is
   * where the check below then refuses a section of no size.
   */
  const t = (y + body.ry) / (2 * body.ry);
  const scaleAt = 1 + (body.taper - 1) * t;
  if (scaleAt <= FLAT) return null;

  return {
    centre: vec3(0, y, 0),
    u: vec3(body.rx * scaleAt, 0, 0),
    v: vec3(0, 0, -body.rz * scaleAt),
  };
}

/**
 * The section of a round solid by a plane *containing* its axis: the "thiết diện qua trục".
 *
 * A triangle for a cone, a rectangle for a cylinder, a trapezium for a frustum — the second of
 * the two sections a paper names, and the one every "thiết diện qua trục là tam giác đều" is
 * about. Exact, and a polygon rather than a curve, because it genuinely is one: the boundary is
 * two generator lines and one or two diameters.
 *
 * `null` when the plane misses the axis, and `null` for a ball, whose axial section is a circle
 * and is answered by {@link roundSection} like every other plane through it.
 */
export function axialSection(body: RoundBody, plane: Plane): readonly Vec3[] | null {
  if (body.ball) return null;

  // The axis has to lie *in* the plane: square to the normal, and passing through it.
  if (Math.abs(dot(plane.normal, UP)) > FLAT) return null;
  if (Math.abs(signedDistance(plane, ZERO)) > FLAT * Math.max(1, body.ry)) return null;

  /*
   * The direction across the solid that lies in the plane. Scaled to the elliptical footprint,
   * so a solid drawn with a squashed base — which every projection of a round one is, and which
   * `depth` lets a user set outright — is cut at its true width in that direction.
   */
  const across = cross(plane.normal, UP);
  const size = length(across);
  if (size < FLAT) return null;

  const unit = scale(across, 1 / size);
  const reach = (at: number): Vec3 => vec3(unit.x * body.rx * at, 0, unit.z * body.rz * at);

  const bottom = 1;
  const top = body.taper;
  const low = -body.ry;
  const high = body.ry;

  const corner = (side: number, y: number, radius: number): Vec3 => {
    const out = reach(side * radius);
    return vec3(out.x, y, out.z);
  };

  // A cone's top edge is a point, so its section is a triangle and not a trapezium with a
  // zero-length side — a shape with a doubled corner reads as a drawing error.
  if (top <= FLAT) {
    return [corner(-1, low, bottom), corner(1, low, bottom), vec3(0, high, 0)];
  }

  return [
    corner(-1, low, bottom),
    corner(1, low, bottom),
    corner(1, high, top),
    corner(-1, high, top),
  ];
}

/** Two perpendicular vectors of the given length, lying in the plane. */
function frameIn(plane: Plane, radius: number): readonly [Vec3, Vec3] {
  // Any direction not along the normal will do; the smallest component of the normal is the
  // safest one to start from, and never nearly parallel to it.
  const n = plane.normal;
  const seed =
    Math.abs(n.x) <= Math.abs(n.y) && Math.abs(n.x) <= Math.abs(n.z)
      ? vec3(1, 0, 0)
      : Math.abs(n.y) <= Math.abs(n.z)
        ? vec3(0, 1, 0)
        : vec3(0, 0, 1);

  const u = normalize(cross(n, seed));
  return [scale(u, radius), scale(cross(n, u), radius)];
}

/** The axis every solid here is built along. */
const UP: Vec3 = { x: 0, y: 1, z: 0 };
const ZERO: Vec3 = { x: 0, y: 0, z: 0 };

/**
 * The part of a line that lies inside a convex mesh, as its two ends.
 *
 * What makes an intersection line drawable. `(SAC) ∩ (SBD)` is the whole line through `S` and
 * the centre of the base, and a figure wants the piece of it inside the pyramid — which is `SO`,
 * the height. Drawing the line itself would run it out through the paper.
 *
 * By clipping against every face's half-space, which for a convex body is exact and needs no
 * ordering: the inside is the intersection of the half-spaces, so the segment is what survives
 * all of them.
 */
export function chordOf(
  mesh: Body,
  line: { origin: Vec3; direction: Vec3 }
): readonly [Vec3, Vec3] | null {
  let low = -Infinity;
  let high = Infinity;

  for (const face of mesh.faces) {
    const plane = faceP(mesh, face);
    if (plane === null) continue;

    const along = dot(plane.normal, line.direction);
    const from = signedDistance(plane, line.origin);

    if (Math.abs(along) < FLAT) {
      // Parallel to this face: either wholly inside its half-space or wholly outside it.
      if (from > 0) return null;
      continue;
    }

    const t = -from / along;
    if (along > 0) high = Math.min(high, t);
    else low = Math.max(low, t);
  }

  if (!(low <= high) || low === -Infinity || high === Infinity) return null;

  const at = (t: number): Vec3 => add(line.origin, scale(line.direction, t));
  const ends: readonly [Vec3, Vec3] = [at(low), at(high)];
  return length(subtract(ends[0], ends[1])) < FLAT ? null : ends;
}

/**
 * The part of a line lying inside a **convex ring in space** — a plane drawn as a parallelogram.
 *
 * The counterpart of {@link chordOf} for a figure with no body. A blackboard plane has no volume
 * to clip against, only the four corners the author chose to draw; the giao tuyến of two of them
 * is the piece inside **both**, which is what a hand draws across the crossing and what a reader
 * takes the line to be.
 *
 * `chordOf` is left exactly as it is. It clips against a body's half-spaces, which is the right
 * answer there and has been proved to be; this is a different question about a different shape,
 * and giving one function two meanings is how the answer to one of them quietly changes.
 *
 * ## The method
 *
 * The ring is flat, so "inside" is decided in the ring's own plane: for each edge, the inward
 * direction is `edgeNormal = cross(ringNormal, edge)`, and a point is inside when it is on the
 * same side as the ring's middle. That is a half-plane per edge, and the line is clipped against
 * each in turn — exact for a convex ring, and needing no ordering.
 *
 * `null` when the line misses the ring, or grazes a corner, or lies in no plane at all.
 */
export function chordInRing(
  ring: readonly Vec3[],
  line: { origin: Vec3; direction: Vec3 }
): readonly [number, number] | null {
  if (ring.length < 3) return null;

  const middle = scale(
    ring.reduce((sum, corner) => add(sum, corner), vec3(0, 0, 0)),
    1 / ring.length
  );

  const [first, second, third] = ring;
  if (first === undefined || second === undefined || third === undefined) return null;
  const face = planeThrough(first, second, third);
  if (face === null) return null;

  let low = -Infinity;
  let high = Infinity;

  for (const [index, corner] of ring.entries()) {
    const next = ring[(index + 1) % ring.length];
    if (next === undefined) continue;

    const edge = subtract(next, corner);
    let inward = cross(face.normal, edge);
    if (length(inward) < FLAT) continue;
    // Point it at the middle, so "inside" means the same for every edge whichever way the ring
    // was wound. A ring walked the other way would otherwise clip to nothing.
    if (dot(inward, subtract(middle, corner)) < 0) inward = scale(inward, -1);

    const along = dot(inward, line.direction);
    const from = dot(inward, subtract(line.origin, corner));

    if (Math.abs(along) < FLAT) {
      // Parallel to this edge: wholly inside its half-plane, or wholly outside it.
      if (from < 0) return null;
      continue;
    }

    const t = -from / along;
    if (along > 0) low = Math.max(low, t);
    else high = Math.min(high, t);
  }

  return low <= high && low > -Infinity && high < Infinity ? [low, high] : null;
}

/** A face's own plane, with its normal pointing out of the body. */
function faceP(mesh: Body, face: readonly number[]): Plane | null {
  const ring = face.flatMap((index) => {
    const point = mesh.vertices[index];
    return point === undefined ? [] : [point];
  });
  if (ring.length < 3) return null;

  const normal = newell(ring);
  const size = length(normal);
  if (size < FLAT) return null;

  const anchor = ring[0];
  if (anchor === undefined) return null;
  return { origin: anchor, normal: scale(normal, 1 / size) };
}

/** Newell's normal: right for a ring of any shape, where a cross product of three is not. */
function newell(ring: readonly Vec3[]): Vec3 {
  let x = 0;
  let y = 0;
  let z = 0;
  for (const [i, a] of ring.entries()) {
    const b = ring[(i + 1) % ring.length];
    if (b === undefined) continue;
    x += (a.y - b.y) * (a.z + b.z);
    y += (a.z - b.z) * (a.x + b.x);
    z += (a.x - b.x) * (a.y + b.y);
  }
  return vec3(x, y, z);
}

/**
 * The points in boundary order, turning about `normal`.
 *
 * They are all on one plane and form a convex ring, so their angle about their own centre *is*
 * the order the boundary visits them — no gift-wrapping, and nothing to go wrong on a tie
 * because a convex ring has no two vertices at the same angle from its interior.
 */
function sortAround(points: readonly Vec3[], normal: Vec3): readonly Vec3[] {
  const middle = scale(
    points.reduce((sum, point) => add(sum, point), vec3(0, 0, 0)),
    1 / points.length
  );

  // Any two perpendicular directions in the plane will do; the ring's own first spoke gives one.
  const first = points.find((point) => length(subtract(point, middle)) > FLAT);
  if (first === undefined) return points;

  const u = normalize(subtract(first, middle));
  const v = cross(normal, u);

  return [...points].sort((a, b) => {
    const da = subtract(a, middle);
    const db = subtract(b, middle);
    return Math.atan2(dot(da, v), dot(da, u)) - Math.atan2(dot(db, v), dot(db, u));
  });
}
